/**
 * One answer to "is a Clerk provider mounted in this build, and how is it
 * configured?".
 *
 * Two different deployments need Clerk for two different reasons:
 *
 * - Local and desktop T3 use it for T3 Connect, which additionally needs a
 *   relay URL and a JWT template. That is `hasCloudPublicConfig()`.
 * - The hosted Roblox Worker uses it only to authenticate same-origin `/api`
 *   requests with the default session token, so a publishable key is the whole
 *   requirement and no relay exists.
 *
 * `main.tsx` mounts the provider on this predicate and every component that
 * calls a Clerk hook checks the same one, so a hook can never run outside a
 * provider. Splitting these two questions is what previously produced a hosted
 * bundle whose components called `useAuth()` with no provider above them.
 */

import { isElectron } from "../env";
import { isHostedStaticApp } from "../hostedPairing";
import { hasCloudPublicConfig, resolveCloudPublicConfig, trimNonEmpty } from "./publicConfig";

/**
 * Where the hosted deployment proxies Clerk's Frontend API by default. Serving
 * Clerk from the app's own origin is what keeps its session cookie first-party
 * on the hosted domain, so hosted mode always uses a same-origin path.
 */
export const DEFAULT_HOSTED_CLERK_PROXY_PATH = "/__clerk";

/**
 * Values that turn the proxy off. Needed because an unset `VITE_*` variable and
 * an empty one are indistinguishable after the build pins them, so "no proxy"
 * has to be said out loud.
 */
const PROXY_DISABLED_VALUES: ReadonlySet<string> = new Set(["off", "none", "disabled", "false"]);

function currentUrl(url: URL | undefined): URL | null {
  if (url) {
    return url;
  }
  return typeof window === "undefined" ? null : new URL(window.location.href);
}

export function clerkPublishableKey(): string | null {
  return resolveCloudPublicConfig().clerkPublishableKey;
}

/**
 * Hosted auth is possible when this bundle is the hosted static app and it
 * carries a publishable key. The Worker verifies the session itself, so no
 * relay or JWT template is involved.
 *
 * Explicitly browser-only: the desktop shell reaches its own bundled server and
 * is never served by the Worker, so excluding Electron keeps the desktop path
 * on exactly the T3 Connect condition it used before this predicate existed.
 */
export function hostedAuthEnabled(url?: URL): boolean {
  if (isElectron) {
    return false;
  }
  const resolved = currentUrl(url);
  return resolved !== null && isHostedStaticApp(resolved) && clerkPublishableKey() !== null;
}

/** True exactly when `main.tsx` renders a `ClerkProvider` around the app. */
export function clerkProviderEnabled(url?: URL): boolean {
  return hasCloudPublicConfig() || hostedAuthEnabled(url);
}

/**
 * True only for the Roblox Worker deployment, which is the one that serves the
 * same-origin Clerk proxy.
 *
 * Hosted-ness alone is not enough: T3's own Vercel app also ships with
 * `VITE_HOSTED_APP_CHANNEL` set and a publishable key, and it does not serve
 * `/__clerk`. Pointing its provider at that path would break sign-in on
 * `app.t3.codes`. What distinguishes the Worker is that it needs no relay: it
 * authenticates its own `/api` routes with the default session token, so it
 * ships a key with no relay URL or JWT template, while T3 Connect always has
 * both.
 */
export function hostedClerkProxyEnabled(url?: URL): boolean {
  if (!hostedAuthEnabled(url)) {
    return false;
  }
  // A build that names the path is taken at its word, which is how the Worker
  // deployment declares itself and stays correct even if it later gains relay
  // config. Everything else gets the proxy only on the relay-free Worker shape.
  return configuredClerkProxyValue() !== null || !hasCloudPublicConfig();
}

function configuredClerkProxyValue(): string | null {
  return trimNonEmpty(import.meta.env.VITE_CLERK_PROXY_URL as string | undefined);
}

/**
 * Normalizes a configured proxy value to a same-origin path.
 *
 * A relative path is taken as-is. An absolute URL is accepted only when it
 * points at the page's own origin, and is reduced to its path so the provider
 * never carries an origin that could drift from the one serving the bundle.
 * Anything else (a different host, a protocol-relative `//host` value, junk)
 * falls back to the default path rather than being honoured: the point of the
 * option is to relocate the same-origin route, not to send Clerk traffic
 * somewhere else.
 */
export function resolveHostedClerkProxyUrl(input?: {
  readonly raw?: string | undefined;
  readonly url?: URL;
}): string | null {
  const raw = input && "raw" in input ? trimNonEmpty(input.raw) : configuredClerkProxyValue();
  if (raw === null) {
    return DEFAULT_HOSTED_CLERK_PROXY_PATH;
  }
  if (PROXY_DISABLED_VALUES.has(raw.toLowerCase())) {
    return null;
  }

  const normalizedPath = (path: string): string => {
    const withoutTrailingSlash = path.replace(/\/+$/, "");
    return withoutTrailingSlash.length > 0 ? withoutTrailingSlash : DEFAULT_HOSTED_CLERK_PROXY_PATH;
  };

  // Protocol-relative values look relative but are not, so they are rejected
  // before the single-slash path check can accept them.
  if (raw.startsWith("/") && !raw.startsWith("//")) {
    return normalizedPath(raw);
  }

  const pageUrl = currentUrl(input?.url);
  try {
    const parsed = new URL(raw, pageUrl?.origin ?? undefined);
    if (pageUrl && parsed.origin === pageUrl.origin) {
      return normalizedPath(parsed.pathname);
    }
  } catch {
    // Fall through to the default below.
  }
  return DEFAULT_HOSTED_CLERK_PROXY_PATH;
}

export interface ClerkProviderConfig {
  readonly publishableKey: string;
  /** Same-origin path Clerk's Frontend API is proxied through, hosted mode only. */
  readonly proxyUrl: string | null;
  /** Whether the T3 Connect relay session provider belongs above the app. */
  readonly relayAuth: boolean;
}

/**
 * The whole provider decision in one place, so `main.tsx` reads as a tree and
 * this file stays the only thing that knows why each piece is there.
 *
 * Returns null exactly when no provider should mount, which is the condition
 * every Clerk-hook caller checks through `clerkProviderEnabled()`.
 */
export function resolveClerkProviderConfig(url?: URL): ClerkProviderConfig | null {
  const publishableKey = clerkPublishableKey();
  if (!publishableKey || !clerkProviderEnabled(url)) {
    return null;
  }
  return {
    publishableKey,
    // Only the Roblox Worker serves a same-origin proxy. T3 Connect, the desktop
    // shell, and T3's own Vercel hosted app all reach Clerk directly, and handing
    // any of them this path would point sign-in at a route that 404s.
    proxyUrl: hostedClerkProxyEnabled(url)
      ? resolveHostedClerkProxyUrl(url ? { url } : undefined)
      : null,
    relayAuth: hasCloudPublicConfig(),
  };
}
